/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Image,
  FlatList,
  Animated,
  ActivityIndicator,
  Share,
  Linking,
} from 'react-native';
import {
  Ionicons,
  MaterialIcons,
  FontAwesome5,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// JOBENG Logo component
const JobengLogo = () => (
  <Image
    source={require('../../../assets/jobeng_icon.png')}
    style={{ width: 30, height: 30 }}
    resizeMode="contain"
  />
);
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

export default function MentorshipProgramDetailsScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // States
  const [isLoading, setIsLoading] = useState(true);
  const [program, setProgram] = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Mock data for mentorship programs (same as in MentorshipProgramsScreen)
  const mockPrograms = [
    {
      id: '1',
      title: 'Engineering Leadership Mentorship',
      organization: 'Women in Engineering UK',
      field: 'Engineering',
      duration: '6 months',
      format: 'One-on-one',
      description:
        'Connect with experienced engineering leaders who will guide you through the early stages of your career. This program focuses on technical skill development, leadership capabilities, and career planning.',
      benefits: [
        'Personalized guidance from industry experts',
        'Technical skill development',
        'Leadership training',
        'Career planning support',
        'Networking opportunities',
      ],
      eligibility: [
        'Early career engineers (0-3 years experience)',
        "Bachelor's degree in Engineering or related field",
        'UK-based professionals',
      ],
      applicationDeadline: '2025-07-15',
      startDate: '2025-08-01',
      logo: 'https://example.com/wie-logo.png',
      isVirtual: true,
      isFree: true,
      commitment: '4-6 hours per month',
      mentors: [
        {
          id: 'm1',
          name: 'Sarah Johnson',
          title: 'Chief Engineer at Rolls-Royce',
          experience: '20+ years',
          expertise: 'Aerospace Engineering, Leadership',
          bio: 'Sarah has led engineering teams at Rolls-Royce for over 15 years, specializing in aerospace propulsion systems. She is passionate about helping early career engineers develop both technical and leadership skills.',
          image: 'https://randomuser.me/api/portraits/women/23.jpg',
        },
        {
          id: 'm2',
          name: 'Michael Chen',
          title: 'Technical Director at Arup',
          experience: '15+ years',
          expertise: 'Civil Engineering, Project Management',
          bio: 'Michael specializes in large-scale infrastructure projects and has managed engineering teams across Europe and Asia. He focuses on helping mentees develop project management skills alongside technical expertise.',
          image: 'https://randomuser.me/api/portraits/men/45.jpg',
        },
      ],
      testimonials: [
        {
          name: 'Emma Wilson',
          role: 'Structural Engineer',
          company: 'Atkins',
          text: 'This mentorship program was transformative for my career. The guidance I received helped me navigate complex technical challenges and develop my leadership skills.',
          image: 'https://randomuser.me/api/portraits/women/33.jpg',
        },
        {
          name: 'James Taylor',
          role: 'Mechanical Engineer',
          company: 'BAE Systems',
          text: 'The mentorship I received was invaluable. My mentor provided practical advice that helped me advance my career and take on more responsibility.',
          image: 'https://randomuser.me/api/portraits/men/22.jpg',
        },
      ],
      faqs: [
        {
          question: 'How much time commitment is required?',
          answer:
            'Mentees are expected to commit 4-6 hours per month, including one-on-one sessions with mentors and independent work.',
        },
        {
          question: 'Can I choose my mentor?',
          answer:
            'Yes, you can indicate your preferred mentors during the application process, though matches are made based on compatibility and availability.',
        },
        {
          question: 'Is there a fee to participate?',
          answer:
            'No, this program is offered free of charge to qualified applicants.',
        },
        {
          question: 'What happens after the program ends?',
          answer:
            'Many mentor-mentee relationships continue informally after the program. We also offer an alumni network for ongoing support.',
        },
      ],
      curriculum: [
        {
          month: 'Month 1',
          focus: 'Goal Setting & Career Planning',
          activities: [
            'Initial mentor-mentee meeting',
            'Career goals assessment',
            'Development plan creation',
          ],
        },
        {
          month: 'Month 2-3',
          focus: 'Technical Skill Development',
          activities: [
            'Technical skills assessment',
            'Specialized training recommendations',
            'Project-based learning',
          ],
        },
        {
          month: 'Month 4-5',
          focus: 'Leadership & Communication',
          activities: [
            'Leadership style assessment',
            'Communication workshops',
            'Team management strategies',
          ],
        },
        {
          month: 'Month 6',
          focus: 'Future Planning & Network Building',
          activities: [
            'Long-term career planning',
            'Networking event',
            'Program completion assessment',
          ],
        },
      ],
    },
    {
      id: '2',
      title: 'Tech Start Mentorship Program',
      organization: 'TechUK',
      field: 'Technology',
      duration: '12 months',
      format: 'Group and one-on-one',
      description:
        'A comprehensive mentorship program designed to help early career tech professionals navigate the industry. Includes both group sessions and one-on-one mentoring with experienced tech leaders.',
      benefits: [
        'Access to senior tech professionals',
        'Monthly group workshops',
        'Individual career coaching',
        'Industry networking events',
        'Resume and interview preparation',
      ],
      eligibility: [
        'Early career tech professionals (0-2 years experience)',
        'Background in Computer Science, Software Engineering, or related fields',
        'Currently employed in a tech role',
      ],
      applicationDeadline: '2025-06-30',
      startDate: '2025-07-15',
      logo: 'https://example.com/techuk-logo.png',
      isVirtual: true,
      isFree: false,
      cost: '£250 (scholarships available)',
      commitment: '8 hours per month',
      mentors: [
        {
          id: 'm3',
          name: 'David Wilson',
          title: 'CTO at Monzo',
          experience: '18+ years',
          expertise: 'Software Architecture, FinTech',
          bio: 'David has led technology teams at several fintech startups before joining Monzo as CTO. He specializes in scalable architecture and engineering leadership in fast-growing companies.',
          image: 'https://randomuser.me/api/portraits/men/32.jpg',
        },
        {
          id: 'm4',
          name: 'Priya Sharma',
          title: 'Engineering Manager at Google',
          experience: '12+ years',
          expertise: 'Cloud Computing, Team Leadership',
          bio: 'Priya leads engineering teams at Google Cloud, focusing on infrastructure and reliability. She is passionate about helping early career engineers develop both technical depth and leadership skills.',
          image: 'https://randomuser.me/api/portraits/women/45.jpg',
        },
      ],
      testimonials: [
        {
          name: 'Alex Johnson',
          role: 'Software Engineer',
          company: 'Deliveroo',
          text: 'The Tech Start program gave me exactly what I needed to accelerate my career. The combination of one-on-one mentoring and group workshops was perfect.',
          image: 'https://randomuser.me/api/portraits/men/67.jpg',
        },
        {
          name: 'Sophia Lee',
          role: 'Frontend Developer',
          company: 'Spotify',
          text: 'My mentor helped me navigate challenges I was facing at work and provided valuable guidance on career progression. The network I built through this program has been invaluable.',
          image: 'https://randomuser.me/api/portraits/women/54.jpg',
        },
      ],
      faqs: [
        {
          question: 'Is financial assistance available?',
          answer:
            'Yes, scholarships are available for applicants who demonstrate financial need. You can apply for a scholarship as part of your program application.',
        },
        {
          question: 'How are mentors matched with mentees?',
          answer:
            'Matches are made based on career goals, technical interests, and personality fit. You can indicate preferences during the application process.',
        },
        {
          question: 'What is the time commitment?',
          answer:
            'Participants should expect to commit approximately 8 hours per month, including one-on-one sessions, group workshops, and independent work.',
        },
        {
          question: 'Is the program fully remote?',
          answer:
            'Yes, all sessions are conducted virtually, making the program accessible regardless of your location.',
        },
      ],
      curriculum: [
        {
          month: 'Month 1-3',
          focus: 'Technical Foundations & Career Planning',
          activities: [
            'Technical skills assessment',
            'Career path exploration',
            'Individual development planning',
          ],
        },
        {
          month: 'Month 4-6',
          focus: 'Specialized Skill Development',
          activities: [
            'Deep dives into technical specialties',
            'Project work with mentor guidance',
            'Technical workshops',
          ],
        },
        {
          month: 'Month 7-9',
          focus: 'Leadership & Collaboration',
          activities: [
            'Team dynamics workshops',
            'Communication skills',
            'Collaborative project work',
          ],
        },
        {
          month: 'Month 10-12',
          focus: 'Career Advancement & Industry Networking',
          activities: [
            'Industry networking events',
            'Interview preparation',
            'Long-term career planning',
          ],
        },
      ],
    },
  ];

  // Load program details when component mounts
  useEffect(() => {
    if (!route.params?.programId) {
      setError('No program ID provided');
      setIsLoading(false);
      return;
    }

    // Simulate API call
    setTimeout(() => {
      try {
        const foundProgram = mockPrograms.find(
          p => p.id === route.params.programId
        );

        if (foundProgram) {
          setProgram(foundProgram);
          setError(null);
        } else {
          setError('Program not found');
        }
      } catch (err) {
        setError('Failed to load program details');
        console.error('Error loading program:', err);
      } finally {
        setIsLoading(false);

        // Animate content in
        Animated.parallel([
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(slideAnim, {
            toValue: 0,
            duration: 500,
            useNativeDriver: true,
          }),
        ]).start();
      }
    }, 1000);
  }, [route.params?.programId, fadeAnim, slideAnim]);

  // Format date
  const formatDate = dateString => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-GB', options);
  };

  // Calculate days until deadline
  const getDaysUntilDeadline = deadlineString => {
    const today = new Date();
    const deadline = new Date(deadlineString);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Handle apply button press
  const handleApply = () => {
    navigation.navigate('MentorshipApplication', { mentorshipId: program.id });
  };

  // Handle share button press
  const handleShare = async () => {
    try {
      await Share.share({
        message: `Check out this mentorship opportunity: ${program.title} by ${program.organization}. Apply by ${formatDate(program.applicationDeadline)}!`,
        title: program.title,
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // Handle save button press
  const handleSave = () => {
    setIsSaved(!isSaved);
    // In a real app, this would save to user's profile or local storage
  };

  // Handle contact organization
  const handleContact = () => {
    // In a real app, this would open email or messaging
    alert('Contact feature would open email or messaging to the organization');
  };

  // Render loading state
  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color="#0e4e96" />
      <Text style={styles.loadingText}>Loading program details...</Text>
    </View>
  );

  // Render error state
  const renderError = () => (
    <View style={styles.errorContainer}>
      <Ionicons name="alert-circle-outline" size={60} color="#EF4444" />
      <Text style={styles.errorTitle}>Error</Text>
      <Text style={styles.errorMessage}>{error}</Text>
      <TouchableOpacity
        style={styles.errorButton}
        onPress={() => navigation.goBack()}
      >
        <Text style={styles.errorButtonText}>Go Back</Text>
      </TouchableOpacity>
    </View>
  );

  // Render mentor item
  const renderMentorItem = ({ item }) => (
    <View style={styles.mentorCard}>
      <Image
        source={{ uri: (item.image && typeof item.image==='string' && item.image.startsWith('http')) ? item.image : undefined }}
        style={styles.mentorImage}
        defaultSource={require('../../../assets/default_avatar.png')}
      />
      <View style={styles.mentorInfo}>
        <Text style={styles.mentorName}>{item.name}</Text>
        <Text style={styles.mentorTitle}>{item.title}</Text>
        <Text style={styles.mentorExpertise}>{item.expertise}</Text>
        <Text style={styles.mentorExperience}>
          {item.experience} experience
        </Text>
        {item.bio && <Text style={styles.mentorBio}>{item.bio}</Text>}
      </View>
    </View>
  );

  // Render testimonial item
  const renderTestimonialItem = ({ item }) => (
    <View style={styles.testimonialCard}>
      <View style={styles.testimonialHeader}>
        <Image
          source={{ uri: (item.image && typeof item.image==='string' && item.image.startsWith('http')) ? item.image : undefined }}
          style={styles.testimonialImage}
          defaultSource={require('../../../assets/default_avatar.png')}
        />
        <View>
          <Text style={styles.testimonialName}>{item.name}</Text>
          <Text style={styles.testimonialRole}>
            {item.role}, {item.company}
          </Text>
        </View>
      </View>
      <Text style={styles.testimonialText}>"{item.text}"</Text>
    </View>
  );

  // Render FAQ item
  const renderFaqItem = ({ item, index }) => (
    <View style={styles.faqItem}>
      <Text style={styles.faqQuestion}>
        {index + 1}. {item.question}
      </Text>
      <Text style={styles.faqAnswer}>{item.answer}</Text>
    </View>
  );

  // Render curriculum item
  const renderCurriculumItem = ({ item }) => (
    <View style={styles.curriculumItem}>
      <Text style={styles.curriculumMonth}>{item.month}</Text>
      <Text style={styles.curriculumFocus}>{item.focus}</Text>
      <View style={styles.curriculumActivities}>
        {item.activities.map((activity, index) => (
          <View key={index} style={styles.activityItem}>
            <View style={styles.activityBullet} />
            <Text style={styles.activityText}>{activity}</Text>
          </View>
        ))}
      </View>
    </View>
  );

  // Render overview tab
  const renderOverviewTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>About This Program</Text>
      <Text style={styles.description}>{program.description}</Text>

      <Text style={styles.sectionTitle}>Key Information</Text>
      <View style={styles.infoGrid}>
        <View style={styles.infoItem}>
          <MaterialCommunityIcons
            name="calendar-range"
            size={24}
            color="#0e4e96"
          />
          <Text style={styles.infoLabel}>Duration</Text>
          <Text style={styles.infoValue}>{program.duration}</Text>
        </View>

        <View style={styles.infoItem}>
          <MaterialCommunityIcons
            name="account-group"
            size={24}
            color="#0e4e96"
          />
          <Text style={styles.infoLabel}>Format</Text>
          <Text style={styles.infoValue}>{program.format}</Text>
        </View>

        <View style={styles.infoItem}>
          <MaterialCommunityIcons
            name="clock-time-four-outline"
            size={24}
            color="#0e4e96"
          />
          <Text style={styles.infoLabel}>Commitment</Text>
          <Text style={styles.infoValue}>{program.commitment}</Text>
        </View>

        <View style={styles.infoItem}>
          <MaterialCommunityIcons
            name={program.isVirtual ? 'laptop' : 'office-building'}
            size={24}
            color="#0e4e96"
          />
          <Text style={styles.infoLabel}>Location</Text>
          <Text style={styles.infoValue}>
            {program.isVirtual ? 'Virtual' : 'In-person'}
          </Text>
        </View>

        <View style={styles.infoItem}>
          <MaterialCommunityIcons
            name="currency-gbp"
            size={24}
            color="#0e4e96"
          />
          <Text style={styles.infoLabel}>Cost</Text>
          <Text style={styles.infoValue}>
            {program.isFree ? 'Free' : program.cost}
          </Text>
        </View>

        <View style={styles.infoItem}>
          <MaterialCommunityIcons
            name="calendar-check"
            size={24}
            color="#0e4e96"
          />
          <Text style={styles.infoLabel}>Start Date</Text>
          <Text style={styles.infoValue}>{formatDate(program.startDate)}</Text>
        </View>
      </View>

      <Text style={styles.sectionTitle}>Benefits</Text>
      <View style={styles.benefitsList}>
        {program.benefits.map((benefit, index) => (
          <View key={index} style={styles.benefitItem}>
            <MaterialIcons name="check-circle" size={20} color="#10B981" />
            <Text style={styles.benefitText}>{benefit}</Text>
          </View>
        ))}
      </View>

      <Text style={styles.sectionTitle}>Eligibility</Text>
      <View style={styles.eligibilityList}>
        {program.eligibility.map((item, index) => (
          <View key={index} style={styles.eligibilityItem}>
            <MaterialIcons name="arrow-right" size={20} color="#0e4e96" />
            <Text style={styles.eligibilityText}>{item}</Text>
          </View>
        ))}
      </View>

      <Text style={styles.sectionTitle}>Organization</Text>
      <View style={styles.organizationContainer}>
        <Image
          source={{ uri: (program.logo && program.logo.startsWith('http')) ? program.logo : undefined }}
          style={styles.organizationLogo}
          defaultSource={require('../../../assets/default_logo.png')}
        />
        <View style={styles.organizationInfo}>
          <Text style={styles.organizationName}>{program.organization}</Text>
          <Text style={styles.organizationField}>{program.field}</Text>
          <TouchableOpacity
            style={styles.organizationLink}
            onPress={() =>
              Linking.openURL(
                `https://www.google.com/search?q=${encodeURIComponent(program.organization)}`
              )
            }
          >
            <Text style={styles.organizationLinkText}>Visit Website</Text>
            <Ionicons name="open-outline" size={16} color="#0e4e96" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  // Render mentors tab
  const renderMentorsTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Meet Your Potential Mentors</Text>
      <Text style={styles.mentorsDescription}>
        These experienced professionals will guide you through the mentorship
        program, providing personalized support and industry insights.
      </Text>

      <FlatList
        data={program.mentors}
        renderItem={renderMentorItem}
        keyExtractor={item => item.id}
        scrollEnabled={false}
        contentContainerStyle={styles.mentorsList}
      />
    </View>
  );

  // Render program tab
  const renderProgramTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Program Curriculum</Text>
      <Text style={styles.programDescription}>
        This structured curriculum is designed to maximize your growth and
        development throughout the mentorship program.
      </Text>

      <FlatList
        data={program.curriculum}
        renderItem={renderCurriculumItem}
        keyExtractor={(item, index) => `curriculum-${index}`}
        scrollEnabled={false}
        contentContainerStyle={styles.curriculumList}
      />

      {program.testimonials && program.testimonials.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Testimonials</Text>
          <FlatList
            data={program.testimonials}
            renderItem={renderTestimonialItem}
            keyExtractor={(item, index) => `testimonial-${index}`}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.testimonialsList}
          />
        </>
      )}

      {program.faqs && program.faqs.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>
          <FlatList
            data={program.faqs}
            renderItem={renderFaqItem}
            keyExtractor={(item, index) => `faq-${index}`}
            scrollEnabled={false}
            contentContainerStyle={styles.faqsList}
          />
        </>
      )}
    </View>
  );

  // Render application tab
  const renderApplicationTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.applicationCard}>
        <Text style={styles.applicationTitle}>Ready to Apply?</Text>
        <Text style={styles.applicationDeadline}>
          Application Deadline: {formatDate(program.applicationDeadline)}
        </Text>

        <View style={styles.deadlineCountdown}>
          <MaterialCommunityIcons
            name="clock-time-four-outline"
            size={24}
            color={
              getDaysUntilDeadline(program.applicationDeadline) < 7
                ? '#EF4444'
                : '#0e4e96'
            }
          />
          <Text
            style={[
              styles.deadlineText,
              getDaysUntilDeadline(program.applicationDeadline) < 7 &&
                styles.urgentDeadline,
            ]}
          >
            {getDaysUntilDeadline(program.applicationDeadline)} days remaining
          </Text>
        </View>

        <Text style={styles.applicationProcess}>Application Process:</Text>
        <View style={styles.processList}>
          <View style={styles.processItem}>
            <View style={styles.processNumber}>
              <Text style={styles.processNumberText}>1</Text>
            </View>
            <View style={styles.processContent}>
              <Text style={styles.processTitle}>Submit Application</Text>
              <Text style={styles.processDescription}>
                Complete the online application form with your personal
                information, background, and mentorship goals.
              </Text>
            </View>
          </View>

          <View style={styles.processConnector} />

          <View style={styles.processItem}>
            <View style={styles.processNumber}>
              <Text style={styles.processNumberText}>2</Text>
            </View>
            <View style={styles.processContent}>
              <Text style={styles.processTitle}>Review Process</Text>
              <Text style={styles.processDescription}>
                Applications are reviewed by the program team. This typically
                takes 2-3 weeks.
              </Text>
            </View>
          </View>

          <View style={styles.processConnector} />

          <View style={styles.processItem}>
            <View style={styles.processNumber}>
              <Text style={styles.processNumberText}>3</Text>
            </View>
            <View style={styles.processContent}>
              <Text style={styles.processTitle}>Interview (if selected)</Text>
              <Text style={styles.processDescription}>
                Selected candidates may be invited for a brief interview to
                discuss goals and expectations.
              </Text>
            </View>
          </View>

          <View style={styles.processConnector} />

          <View style={styles.processItem}>
            <View style={styles.processNumber}>
              <Text style={styles.processNumberText}>4</Text>
            </View>
            <View style={styles.processContent}>
              <Text style={styles.processTitle}>Final Selection</Text>
              <Text style={styles.processDescription}>
                Successful applicants will be notified and matched with
                appropriate mentors.
              </Text>
            </View>
          </View>
        </View>

        <TouchableOpacity style={styles.applyButton} onPress={handleApply}>
          <Text style={styles.applyButtonText}>Apply Now</Text>
          <Ionicons name="arrow-forward" size={20} color="#fff" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.contactButton} onPress={handleContact}>
          <Text style={styles.contactButtonText}>Contact Organization</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'Overview':
        return renderOverviewTab();
      case 'Mentors':
        return renderMentorsTab();
      case 'Program':
        return renderProgramTab();
      case 'Application':
        return renderApplicationTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Program Details</Text>
        <JobengLogo />
      </View>

      {isLoading ? (
        renderLoading()
      ) : error ? (
        renderError()
      ) : (
        <ScrollView
          style={styles.container}
          showsVerticalScrollIndicator={false}
        >
          <Animated.View
            style={[
              styles.content,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            {/* Program Header */}
            <View style={styles.programHeader}>
              <View style={styles.programTitleContainer}>
                <Text style={styles.programTitle}>{program.title}</Text>
                <Text style={styles.programOrganization}>
                  {program.organization}
                </Text>

                <View style={styles.programMeta}>
                  <View style={styles.metaItem}>
                    <MaterialCommunityIcons
                      name="domain"
                      size={16}
                      color="#64748B"
                    />
                    <Text style={styles.metaText}>{program.field}</Text>
                  </View>

                  <View style={styles.metaItem}>
                    <MaterialCommunityIcons
                      name="calendar-range"
                      size={16}
                      color="#64748B"
                    />
                    <Text style={styles.metaText}>{program.duration}</Text>
                  </View>

                  <View style={styles.metaItem}>
                    <MaterialCommunityIcons
                      name={program.isVirtual ? 'laptop' : 'office-building'}
                      size={16}
                      color="#64748B"
                    />
                    <Text style={styles.metaText}>
                      {program.isVirtual ? 'Virtual' : 'In-person'}
                    </Text>
                  </View>
                </View>

                <View style={styles.deadlineBadge}>
                  <MaterialCommunityIcons
                    name="calendar-clock"
                    size={14}
                    color="#fff"
                  />
                  <Text style={styles.deadlineBadgeText}>
                    Apply by {formatDate(program.applicationDeadline)}
                  </Text>
                </View>
              </View>

              <View style={styles.programActions}>
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={handleShare}
                >
                  <Ionicons
                    name="share-social-outline"
                    size={22}
                    color="#64748B"
                  />
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={handleSave}
                >
                  <Ionicons
                    name={isSaved ? 'bookmark' : 'bookmark-outline'}
                    size={22}
                    color={isSaved ? '#0e4e96' : '#64748B'}
                  />
                </TouchableOpacity>
              </View>
            </View>

            {/* Tab Navigation */}
            <View style={styles.tabContainer}>
              {['Overview', 'Mentors', 'Program', 'Application'].map(tab => (
                <TouchableOpacity
                  key={tab}
                  style={[
                    styles.tabButton,
                    activeTab === tab && styles.activeTabButton,
                  ]}
                  onPress={() => setActiveTab(tab)}
                >
                  <Text
                    style={[
                      styles.tabText,
                      activeTab === tab && styles.activeTabText,
                    ]}
                  >
                    {tab}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Tab Content */}
            {renderTabContent()}

            {/* Apply Button (Fixed at bottom) */}
            <View style={styles.bottomButtonContainer}>
              <TouchableOpacity
                style={styles.bottomApplyButton}
                onPress={handleApply}
              >
                <Text style={styles.bottomApplyButtonText}>Apply Now</Text>
              </TouchableOpacity>
            </View>
          </Animated.View>
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },

  // Loading and Error States
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 10,
  },
  errorMessage: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 10,
    marginBottom: 20,
  },
  errorButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  errorButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },

  // Program Header
  programHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  programTitleContainer: {
    flex: 1,
  },
  programTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  programOrganization: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 8,
  },
  programMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 4,
  },
  metaText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 4,
  },
  deadlineBadge: {
    backgroundColor: '#0e4e96',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 16,
    alignSelf: 'flex-start',
  },
  deadlineBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
  },
  programActions: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  actionButton: {
    padding: 8,
    marginLeft: 8,
  },

  // Tab Navigation
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 8,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
  },
  activeTabButton: {
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    color: '#64748B',
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: 'bold',
  },

  // Tab Content
  tabContent: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  description: {
    fontSize: 15,
    color: '#333',
    lineHeight: 22,
    marginBottom: 20,
  },

  // Info Grid
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  infoItem: {
    width: '48%',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    alignItems: 'center',
  },
  infoLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 6,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginTop: 2,
    textAlign: 'center',
  },

  // Benefits List
  benefitsList: {
    marginBottom: 20,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  benefitText: {
    fontSize: 15,
    color: '#333',
    marginLeft: 10,
    flex: 1,
  },

  // Eligibility List
  eligibilityList: {
    marginBottom: 20,
  },
  eligibilityItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  eligibilityText: {
    fontSize: 15,
    color: '#333',
    marginLeft: 10,
    flex: 1,
  },

  // Organization
  organizationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  organizationLogo: {
    width: 60,
    height: 60,
    borderRadius: 8,
    backgroundColor: '#e2e8f0',
  },
  organizationInfo: {
    marginLeft: 16,
    flex: 1,
  },
  organizationName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 2,
  },
  organizationField: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  organizationLink: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  organizationLinkText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '500',
    marginRight: 4,
  },

  // Mentors Tab
  mentorsDescription: {
    fontSize: 15,
    color: '#333',
    lineHeight: 22,
    marginBottom: 16,
  },
  mentorsList: {
    paddingBottom: 8,
  },
  mentorCard: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  mentorImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#e2e8f0',
  },
  mentorInfo: {
    marginLeft: 16,
    flex: 1,
  },
  mentorName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 2,
  },
  mentorTitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  mentorExpertise: {
    fontSize: 14,
    color: '#333',
    marginBottom: 2,
  },
  mentorExperience: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  mentorBio: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },

  // Program Tab
  programDescription: {
    fontSize: 15,
    color: '#333',
    lineHeight: 22,
    marginBottom: 16,
  },
  curriculumList: {
    marginBottom: 20,
  },
  curriculumItem: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  },
  curriculumMonth: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 4,
  },
  curriculumFocus: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  curriculumActivities: {
    marginLeft: 4,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  activityBullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#0e4e96',
    marginRight: 8,
  },
  activityText: {
    fontSize: 14,
    color: '#333',
    flex: 1,
  },

  // Testimonials
  testimonialsList: {
    paddingRight: 16,
    marginBottom: 20,
  },
  testimonialCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 16,
    marginRight: 12,
    width: 280,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  testimonialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  testimonialImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e2e8f0',
    marginRight: 12,
  },
  testimonialName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
  },
  testimonialRole: {
    fontSize: 12,
    color: '#64748B',
  },
  testimonialText: {
    fontSize: 14,
    color: '#333',
    fontStyle: 'italic',
    lineHeight: 20,
  },

  // FAQs
  faqsList: {
    marginBottom: 8,
  },
  faqItem: {
    marginBottom: 16,
  },
  faqQuestion: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  faqAnswer: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },

  // Application Tab
  applicationCard: {
    padding: 8,
  },
  applicationTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  applicationDeadline: {
    fontSize: 16,
    color: '#333',
    marginBottom: 8,
  },
  deadlineCountdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 20,
    alignSelf: 'flex-start',
  },
  deadlineText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0e4e96',
    marginLeft: 8,
  },
  urgentDeadline: {
    color: '#EF4444',
  },
  applicationProcess: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  processList: {
    marginBottom: 24,
  },
  processItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  processNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  processNumberText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  processContent: {
    flex: 1,
  },
  processTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  processDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  processConnector: {
    width: 2,
    height: 20,
    backgroundColor: '#0e4e96',
    marginLeft: 14,
    marginBottom: 8,
  },
  applyButton: {
    backgroundColor: '#0e4e96',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 8,
    marginBottom: 12,
  },
  applyButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    marginRight: 8,
  },
  contactButton: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#0e4e96',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  contactButtonText: {
    color: '#0e4e96',
    fontSize: 16,
    fontWeight: '600',
  },

  // Bottom Apply Button
  bottomButtonContainer: {
    marginTop: 16,
    marginBottom: 40,
  },
  bottomApplyButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  bottomApplyButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
});
